// Use the consolidated main backend URL
const BACKEND_BASE_URL = process.env.NEXT_PUBLIC_MAIN_BACKEND_BASE_URL;

export const fetchPromotions = async (
    entity_id: string,
    page_id: string,
    variant_id?: string
) => {
    try {
        const params = new URLSearchParams({ entity_id, page_id });
        if (variant_id) params.set('variant_id', variant_id);
        const res = await fetch(`${BACKEND_BASE_URL}/api/pricing-pages/public?${params.toString()}`);

        if (res.status === 404) {
            const error = new Error('Promotion not found');
            (error as any).status = 404;
            throw error;
        }

        if (!res.ok) {
            throw new Error(`Failed to fetch promotion: ${res.status}`);
        }

        const responseData = await res.json();
        // Extract pricing_page from response (backend wraps in data.pricing_page)
        const data = responseData.data?.pricing_page || responseData.pricing_page || responseData;

        // Map id to promotion_id for v3.ts compatibility
        if (data.id && !data.promotion_id) {
            data.promotion_id = data.id;
        }

        // Transform items: flatten prices[0] into item level for v3.ts compatibility
        if (data.items && Array.isArray(data.items)) {
            data.items = data.items.map((item: any) => {
                const price = item.prices?.[0];
                if (price) {
                    return {
                        ...item,
                        amount: price.amount,
                        currency: price.currency,
                        quantity: price.quantity,
                        billing_frequency: price.billing_frequency,
                    };
                }
                return item;
            });
        }

        return data;
    } catch (error) {
        console.error('Error fetching promotions:', error);
        throw error;
    }
};

export const verifyCoupon = async (promotion_id: string, coupon_code: string) => {
    try {
        const url = `${BACKEND_BASE_URL}/api/pricing-pages/${promotion_id}/coupons/public-verify?coupon_code=${coupon_code}`;

        const res = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        });

        const data = await res.text();

        if (!res.ok) {
            throw new Error(`Coupon verification failed: ${res.status} - ${data}`);
        }

        try {
            const jsonData = JSON.parse(data);
            return jsonData;
        } catch (parseError) {
            console.error('Failed to parse JSON response:', parseError);
            throw new Error(`Invalid JSON response: ${data}`);
        }
    } catch (error) {
        console.error('Error verifying coupon:', error);
        throw error;
    }
};

export const requestTransactionIntent = async (entityId: string, pageId: string, itemId: string, name: string, email: string, successUrl: string, cancelUrl: string, couponCode?: string, isCheckout = true) => {
    try {
        const body: Record<string, any> = {
            entity_id: entityId,
            page_id: pageId,
            item_id: itemId,
            name,
            email,
            is_checkout: isCheckout,
        };

        // Only include URLs for checkout mode
        if (isCheckout) {
            body.success_url = successUrl;
            body.cancel_url = cancelUrl;
        }

        // Only include coupon_code if it has a value (backend rejects null)
        if (couponCode && couponCode.trim()) {
            body.coupon_code = couponCode;
        }

        const res = await fetch(`${BACKEND_BASE_URL}/api/transaction-intents`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const response = await res.json();
        return response;
    } catch (error) {
        console.error('Error fetching client secret:', error);
        throw error;
    }
};

export const requestPayPalTransactionIntent = async (
    entityId: string,
    pageId: string,
    itemId: string,
    name: string,
    email: string,
    couponCode?: string
) => {
    try {
        const body: Record<string, any> = {
            entity_id: entityId,
            page_id: pageId,
            item_id: itemId,
            email,
            name,
        };

        if (couponCode && couponCode.trim()) {
            body.coupon_code = couponCode;
        }

        const res = await fetch(`${BACKEND_BASE_URL}/api/paypal/transaction-intents`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.message || `PayPal transaction intent failed: ${res.status}`);
        }

        const response = await res.json();
        return response.data || response;
    } catch (error) {
        console.error('Error creating PayPal transaction intent:', error);
        throw error;
    }
};

export const capturePayPalOrder = async (orderId: string) => {
    try {
        const res = await fetch(`${BACKEND_BASE_URL}/api/paypal/capture-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: orderId }),
        });

        if (!res.ok) {
            const errorData = await res.json().catch(() => ({}));
            throw new Error(errorData.message || `PayPal capture failed: ${res.status}`);
        }

        const response = await res.json();
        return response.data || response;
    } catch (error) {
        console.error('Error capturing PayPal order:', error);
        throw error;
    }
};

export const submitSignup = async (
    app_id: string,
    promotion_id: string,
    item_id: string,
    name: string,
    email: string,
    couponCode?: string,
    paymentInfo?: Record<string, any>,
    metadata?: Record<string, string>,
    splitTestContext?: { split_test_id: string; split_test_variant_id: string }) => {
    try {
        const res = await fetch(`${BACKEND_BASE_URL}/api/user-transactions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                app_id,
                pricing_page_id: promotion_id,
                item_id,
                name,
                email,
                ...(paymentInfo && { payment_info: paymentInfo }),
                ...(couponCode && { coupon_code: couponCode }),
                ...(metadata && { metadata }),
                ...(splitTestContext && {
                    split_test_id: splitTestContext.split_test_id,
                    split_test_variant_id: splitTestContext.split_test_variant_id,
                }),
                payout_status: 'payout_pending',
            }),
        });

        if (!res.ok) {
            throw new Error(`Failed to submit lead: ${res.status}`);
        }

        const data = await res.json();
        return data;
    } catch (error) {
        console.error('Error submitting lead:', error);
        throw error;
    }
};

export const sendContactEmail = async (
    promotion_id: string,
    name: string,
    email: string,
    message: string
) => {
    try {
        const res = await fetch(`${BACKEND_BASE_URL}/api/contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                promotion_id,
                name,
                email,
                message
            }),
        });

        if (!res.ok) {
            throw new Error(`Failed to send contact email: ${res.status}`);
        }

        const data = await res.json();
        return data;
    } catch (error) {
        console.error('Error sending contact email:', error);
        // If API endpoint doesn't exist, we'll simulate success for now
        // In production, you should implement a proper email service
        return { success: true, message: 'Contact form submitted successfully' };
    }
};

/**
 * Published 템플릿 조회
 * 커스텀 템플릿이 있으면 반환, 없으면 null (레거시 모드)
 */
export const fetchPublishedTemplate = async (
    pageId: string
): Promise<{ template_data: string } | null> => {
    try {
        const res = await fetch(
            `${BACKEND_BASE_URL}/api/pricing-pages/${pageId}/published`
        );

        if (res.status === 404) {
            // 템플릿 없음 - 정상 케이스 (레거시 모드로 fallback)
            return null;
        }

        if (!res.ok) {
            console.warn(`Failed to fetch template: ${res.status}`);
            return null;
        }

        const data = await res.json();
        return data.template || null;
    } catch (error) {
        // API 실패해도 레거시 모드로 fallback (사용자 경험 유지)
        console.warn('Error fetching template:', error);
        return null;
    }
};

// Split test API response interfaces
export interface SplitTestVariant {
    id: string;
    split_test_id: string;
    pricing_page_id: string;
    distribution_percent: number;
    sort_order: number;
    pricing_page?: {
        id: string;
        page_id: string;
        entity_id: string;
        name: string;
        [key: string]: any;
    };
}

export interface SplitTestResponse {
    id: string;
    name: string;
    member_id: string;
    status: 'DRAFT' | 'RUNNING' | 'STOPPED';
    created_at: string;
    updated_at: string;
    started_at: string | null;
    stopped_at: string | null;
    slug: string | null;
    variants: SplitTestVariant[];
}

// Fetch split test by ID
export const fetchSplitTest = async (testId: string): Promise<SplitTestResponse> => {
    try {
        const res = await fetch(
            `${BACKEND_BASE_URL}/api/split-tests/${testId}/public`
        );

        if (!res.ok) {
            throw new Error(`Failed to fetch split test: ${res.status}`);
        }

        const data = await res.json();
        return data.data || data;
    } catch (error) {
        console.error('Error fetching split test:', error);
        throw error;
    }
};

// Record a split test view.
// Continues to call the deprecated /split-tests/:id/views endpoint, which
// the server now routes through the same recordPageView service that backs
// /api/pageviews. Will be migrated to /api/pageviews directly once the
// split-test variant response includes the resolved PricingPage UUID.
export const recordSplitTestView = async (
    testId: string,
    variantId: string,
    visitorId: string,
    source: 'web' | 'embed' = 'embed'
): Promise<void> => {
    try {
        await fetch(`${BACKEND_BASE_URL}/api/split-tests/${testId}/views`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ variant_id: variantId, visitor_id: visitorId, source }),
        });
    } catch (error) {
        console.error('Error recording split test view:', error);
        // Don't throw - view tracking is non-critical
    }
};

// Fire a direct (non-split-test) pageview for analytics.
// Uses `fetch` with `keepalive: true` so the request survives navigation
// without the CORS quirks that `navigator.sendBeacon` has with
// `application/json` payloads (Chrome silently fails preflight for
// non-simple content types under sendBeacon). Non-blocking, best-effort.
export interface PageViewPayload {
    pricing_page_id: string;
    visitor_id: string;
    source: 'web' | 'embed';
    split_test_id?: string;
    variant_id?: string;
    referrer?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
}

export const recordPageView = (payload: PageViewPayload): void => {
    if (!BACKEND_BASE_URL) return;
    const url = `${BACKEND_BASE_URL}/api/pageviews`;
    try {
        void fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {});
    } catch (error) {
        console.error('Error recording pageview:', error);
        // Non-critical, never throw
    }
};
